/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { LoggingWindow } from "./LoggingWindow";

/**
 * Single runtime owner of the current Service-API logging window.
 *
 * The window is written only by the config-apply / restore paths.
 * {@link isServiceApiLoggingActive} re-evaluates the window against the
 * current wall-clock time on every call, so Service-API logging
 * self-expires at the window edges without any further apply — an
 * expired or not-yet-started window reads as off.
 */
export class ServiceApiLoggingStateProvider {
  private static readonly MS_PER_SECOND: number = 1000;

  private static sWindow: LoggingWindow | null = null;

  /**
   * Sets the active window. A null window disables Service-API logging.
   *
   * @param window - The window to honour, or null to disable
   */
  public static setWindow(window: LoggingWindow | null): void {
    ServiceApiLoggingStateProvider.sWindow = window;
  }

  /**
   * Whether Service-API logging is enabled at the current wall-clock time.
   * Re-evaluates on every call; never caches a boolean.
   *
   * @returns true when a window is set and the current time is inside it
   */
  public static isServiceApiLoggingActive(): boolean {
    if (ServiceApiLoggingStateProvider.sWindow === null) {
      return false;
    }

    const nowEpochSec: number = Math.floor(
      Date.now() / ServiceApiLoggingStateProvider.MS_PER_SECOND
    );
    return ServiceApiLoggingStateProvider.sWindow.isInWindow(nowEpochSec);
  }
}
